import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardDescription, CardFooter, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Checkbox } from '../components/ui/checkbox';
import { getCollections, createCollection, updateCollection, deleteCollection, getPosts, addPostToCollection, removePostFromCollection } from '../services/api';
import { FolderOpen, Plus, Edit, Trash2, DollarSign, Eye, Archive, Shield } from 'lucide-react';
import { toast } from '../hooks/use-toast';

const CollectionsPage = () => {
  const { isOwner } = useAuth();
  const navigate = useNavigate();
  const [collections, setCollections] = useState([]);
  const [allPosts, setAllPosts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editingCollection, setEditingCollection] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [isPostDialogOpen, setIsPostDialogOpen] = useState(false);
  const [selectedCollection, setSelectedCollection] = useState(null);
  const [selectedPosts, setSelectedPosts] = useState([]);

  const [formData, setFormData] = useState({
    title: '',
    description: '',
    thumbnail: '',
    price: null,
    isPublished: false
  });

  const loadCollections = useCallback(async () => {
    try {
      setLoading(true);
      const data = await getCollections(false);
      setCollections(data);
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to load collections',
        variant: 'destructive'
      });
    } finally {
      setLoading(false);
    }
  }, []);

  const loadPosts = useCallback(async () => {
    try {
      const data = await getPosts();
      setAllPosts(data);
    } catch (error) {
    }
  }, []);

  useEffect(() => {
    if (isOwner) {
      loadCollections();
      loadPosts();
    }
  }, [isOwner, loadCollections, loadPosts]);

  const handleCreateOrUpdate = async (e) => {
    e.preventDefault();

    try {
      if (editingCollection) {
        await updateCollection(editingCollection.id, formData);
        toast({
          title: 'Success',
          description: 'Collection updated successfully'
        });
      } else {
        await createCollection(formData);
        toast({
          title: 'Success',
          description: 'Collection created successfully'
        });
      }

      setIsDialogOpen(false);
      resetForm();
      loadCollections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to save collection',
        variant: 'destructive'
      });
    }
  };

  const handleEdit = (collection) => {
    setEditingCollection(collection);
    setFormData({
      title: collection.title,
      description: collection.description || '',
      thumbnail: collection.thumbnail || '',
      price: collection.price,
      isPublished: collection.isPublished
    });
    setIsDialogOpen(true);
  };

  const handleDelete = async (collectionId) => {
    if (!window.confirm('Are you sure you want to delete this collection?')) return;

    try {
      await deleteCollection(collectionId);
      toast({
        title: 'Success',
        description: 'Collection deleted successfully'
      });
      loadCollections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to delete collection',
        variant: 'destructive'
      });
    }
  };

  const handleManagePosts = (collection) => {
    setSelectedCollection(collection);
    setSelectedPosts(collection.postIds || []);
    setIsPostDialogOpen(true);
  };

  const handleSavePosts = async () => {
    if (!selectedCollection) return;

    try {
      const currentPostIds = selectedCollection.postIds || [];
      const toAdd = selectedPosts.filter(id => !currentPostIds.includes(id));
      const toRemove = currentPostIds.filter(id => !selectedPosts.includes(id));

      for (const postId of toAdd) {
        await addPostToCollection(selectedCollection.id, postId);
      }

      for (const postId of toRemove) {
        await removePostFromCollection(selectedCollection.id, postId);
      }

      toast({
        title: 'Success',
        description: 'Collection posts updated successfully'
      });

      setIsPostDialogOpen(false);
      loadCollections();
    } catch (error) {
      toast({
        title: 'Error',
        description: 'Failed to update collection posts',
        variant: 'destructive'
      });
    }
  };

  const togglePostSelection = (postId) => {
    setSelectedPosts(prev =>
      prev.includes(postId) ? prev.filter(id => id !== postId) : [...prev, postId]
    );
  };

  const resetForm = () => {
    setFormData({
      title: '',
      description: '',
      thumbnail: '',
      price: null,
      isPublished: false
    });
    setEditingCollection(null);
  };

  if (!isOwner) {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Shield className="w-16 h-16 mx-auto text-red-600 mb-4" />
          <h1 className="text-2xl font-headline text-amber-500 mb-4 tracking-wider">ACCESS DENIED</h1>
          <p className="text-gray-400 font-typewriter mb-6">This area is restricted to the platform owner.</p>
          <Button onClick={() => navigate('/feed')} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
            Back to Evidence Files
          </Button>
        </div>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="min-h-screen bg-black relative">
        <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
        <div className="container mx-auto px-4 py-16 text-center relative z-10">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-amber-500 mx-auto"></div>
          <p className="mt-4 text-amber-500/70 font-typewriter">Loading case archives...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black relative" data-testid="collections-page">
      {/* Texture overlay */}
      <div className="absolute inset-0 opacity-5 bg-[url('data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHdpZHRoPSI2MCIgaGVpZ2h0PSI2MCI+PHBhdGggZD0iTTAgMGg2MHY2MEgweiIgZmlsbD0iIzAwMCIvPjxwYXRoIGQ9Ik0zMCAzMG0tMiAwYTIgMiAwIDEgMSA0IDBhMiAyIDAgMSAxLTQgMCIgZmlsbD0iI2ZmZiIvPjwvc3ZnPg==')]"></div>
      
      <div className="container mx-auto px-4 py-8 max-w-7xl relative z-10">
        <div className="mb-8 flex items-center justify-between">
          <div>
            <div className="inline-block mb-4">
              <div className="bg-amber-900/20 border-2 border-amber-600 px-6 py-2 inline-flex items-center gap-3">
                <Archive className="w-6 h-6 text-amber-500" />
                <h1 className="text-3xl font-headline text-amber-500 tracking-wider" data-testid="collections-title">CASE ARCHIVES</h1>
              </div>
            </div>
            <p className="text-gray-400 font-typewriter">Organize and sell your case files in curated collections</p>
          </div>
          <Dialog open={isDialogOpen} onOpenChange={(open) => {
            setIsDialogOpen(open);
            if (!open) resetForm();
          }}>
            <DialogTrigger asChild>
              <Button className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider" data-testid="create-collection-btn">
                <Plus className="w-4 h-4 mr-2" />
                Create Archive
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-2xl bg-gray-900 border-2 border-amber-900/50">
              <DialogHeader>
                <DialogTitle className="font-headline text-xl text-amber-500 tracking-wider">
                  {editingCollection ? 'EDIT ARCHIVE' : 'CREATE NEW ARCHIVE'}
                </DialogTitle>
                <DialogDescription className="font-typewriter text-gray-400">
                  Create organized collections of case files that can be free or paid
                </DialogDescription>
              </DialogHeader>
              <form onSubmit={handleCreateOrUpdate}>
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="title" className="font-headline text-amber-500 tracking-wide">Archive Title *</Label>
                    <Input
                      id="title"
                      value={formData.title}
                      onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                      placeholder="Behind the Scenes"
                      required
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="description" className="font-headline text-amber-500 tracking-wide">Description</Label>
                    <Textarea
                      id="description"
                      value={formData.description}
                      onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                      placeholder="Exclusive behind-the-scenes evidence..."
                      rows={3}
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="thumbnail" className="font-headline text-amber-500 tracking-wide">Thumbnail URL</Label>
                    <Input
                      id="thumbnail"
                      value={formData.thumbnail}
                      onChange={(e) => setFormData({ ...formData, thumbnail: e.target.value })}
                      placeholder="https://example.com/image.jpg"
                      className="bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="price" className="font-headline text-amber-500 tracking-wide">Price (leave empty for free)</Label>
                    <div className="relative">
                      <DollarSign className="absolute left-3 top-1/2 transform -translate-y-1/2 text-amber-500 w-4 h-4" />
                      <Input
                        id="price"
                        type="number"
                        step="0.01"
                        min="0"
                        value={formData.price || ''}
                        onChange={(e) => setFormData({ ...formData, price: e.target.value ? parseFloat(e.target.value) : null })}
                        placeholder="9.99"
                        className="pl-10 bg-gray-800 border-amber-900/50 focus:border-amber-600 font-typewriter"
                      />
                    </div>
                    <p className="text-xs text-gray-500 font-typewriter">
                      Set a price to sell this archive. Leave empty for free access.
                    </p>
                  </div>

                  <div className="flex items-center space-x-2 p-4 bg-amber-900/10 border border-amber-900/30 rounded-lg">
                    <Checkbox
                      id="isPublished"
                      checked={formData.isPublished}
                      onCheckedChange={(checked) => setFormData({ ...formData, isPublished: checked })}
                    />
                    <Label htmlFor="isPublished" className="cursor-pointer font-typewriter text-gray-300">
                      Publish archive (make it visible to investigators)
                    </Label>
                  </div>
                </div>

                <DialogFooter className="mt-6">
                  <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider">
                    Cancel
                  </Button>
                  <Button type="submit" className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
                    {editingCollection ? 'Update Archive' : 'Create Archive'}
                  </Button>
                </DialogFooter>
              </form>
            </DialogContent>
          </Dialog>
        </div>

        {/* Collections Grid */}
        {collections.length === 0 ? (
          <Card className="py-16 bg-gray-900/90 border-2 border-amber-900/30">
            <CardContent className="text-center">
              <Archive className="w-16 h-16 mx-auto text-amber-900/50 mb-4" />
              <h3 className="text-xl font-headline text-amber-500 mb-2 tracking-wider">NO ARCHIVES YET</h3>
              <p className="text-gray-400 font-typewriter mb-4">
                Create your first archive to organize your case files
              </p>
              <Button onClick={() => setIsDialogOpen(true)} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
                <Plus className="w-4 h-4 mr-2" />
                Create Archive
              </Button>
            </CardContent>
          </Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {collections.map((collection) => (
              <Card key={collection.id} className="overflow-hidden hover:shadow-[0_0_20px_rgba(251,191,36,0.2)] transition-all bg-gray-900/90 border-2 border-amber-900/30 hover:border-amber-700/50" data-testid={`collection-card-${collection.id}`}>
                {collection.thumbnail && (
                  <div className="h-48 overflow-hidden bg-gray-800 relative">
                    <img
                      src={collection.thumbnail}
                      alt={collection.title}
                      className="w-full h-full object-cover grayscale-[30%]"
                      style={{filter: 'sepia(0.2) contrast(1.05)'}}
                    />
                    {/* Archive tab */}
                    <div className="absolute top-2 left-2 bg-amber-700/90 text-black px-3 py-1 text-xs font-headline tracking-wider">
                      ARCHIVE #{collection.id?.slice(-4) || '0001'}
                    </div>
                  </div>
                )}
                <CardHeader>
                  <div className="flex items-start justify-between">
                    <CardTitle className="text-lg font-headline text-gray-100 tracking-wide">{collection.title}</CardTitle>
                    {!collection.isPublished && (
                      <Badge variant="outline" className="border-amber-600 text-amber-500 font-headline tracking-wider">DRAFT</Badge>
                    )}
                  </div>
                  {collection.description && (
                    <CardDescription className="line-clamp-2 font-typewriter text-gray-400">
                      {collection.description}
                    </CardDescription>
                  )}
                </CardHeader>
                <CardContent>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500 font-typewriter">
                      {collection.postIds?.length || 0} case files
                    </span>
                    {collection.price ? (
                      <Badge className="bg-green-600 font-headline tracking-wider">${collection.price}</Badge>
                    ) : (
                      <Badge variant="secondary" className="font-headline tracking-wider">FREE</Badge>
                    )}
                  </div>
                </CardContent>
                <CardFooter className="flex gap-2 border-t border-amber-900/30 pt-4">
                  <Button variant="outline" size="sm" onClick={() => handleManagePosts(collection)} className="flex-1 border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider">
                    <Eye className="w-4 h-4 mr-1" />
                    Files
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleEdit(collection)} className="border-amber-900 text-amber-500 hover:bg-amber-900/20">
                    <Edit className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={() => handleDelete(collection.id)} className="border-red-900 text-red-500 hover:bg-red-900/20">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                </CardFooter>
              </Card>
            ))}
          </div>
        )}

        {/* Manage Posts Dialog */}
        <Dialog open={isPostDialogOpen} onOpenChange={setIsPostDialogOpen}>
          <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto bg-gray-900 border-2 border-amber-900/50">
            <DialogHeader>
              <DialogTitle className="font-headline text-xl text-amber-500 tracking-wider">MANAGE CASE FILES: {selectedCollection?.title}</DialogTitle>
              <DialogDescription className="font-typewriter text-gray-400">
                Select case files to include in this archive
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-2">
              {allPosts.map((post) => (
                <div
                  key={post.id}
                  className="flex items-center space-x-3 p-3 border border-amber-900/30 rounded-lg hover:bg-amber-900/10 cursor-pointer transition-all"
                  onClick={() => togglePostSelection(post.id)}
                >
                  <Checkbox
                    checked={selectedPosts.includes(post.id)}
                    onCheckedChange={() => togglePostSelection(post.id)}
                  />
                  {post.images?.[0] && (
                    <img src={post.images[0]} alt={post.title} className="w-16 h-16 object-cover rounded grayscale-[30%]" style={{filter: 'sepia(0.2)'}} />
                  )}
                  <div className="flex-1">
                    <p className="font-headline text-gray-100 tracking-wide">{post.title}</p>
                    <p className="text-sm text-gray-500 line-clamp-1 font-typewriter">{post.content}</p>
                  </div>
                </div>
              ))}
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setIsPostDialogOpen(false)} className="border-amber-900 text-amber-500 hover:bg-amber-900/20 font-headline tracking-wider">
                Cancel
              </Button>
              <Button onClick={handleSavePosts} className="bg-amber-600 hover:bg-amber-700 text-black font-headline tracking-wider">
                Save Changes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
};

export default CollectionsPage;
